// PROTOTYPE-ONLY (owner login) — CP2
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { Lock, Mail, AlertCircle, Loader2 } from "lucide-react";
import { ForgotPasswordModal, ForgotEmailModal } from "../components/AuthRecoveryModals";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showFP, setShowFP] = useState(false);
  const [showFE, setShowFE] = useState(false);

  useEffect(() => {
    // Bail out if a password recovery is in progress — never auto-open dashboard.
    try {
      if (sessionStorage.getItem("spotbounty_password_recovery_active") === "true") return;
    } catch { /* ignore */ }
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) nav("/admin", { replace: true });
    });
  }, [nav]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError("");
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) { setError(error.message || "Login failed."); return; }
    if (!data?.session) { setError("No session returned."); return; }
    nav("/admin");
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex flex-col">
      <Seo title="Admin Login" description="SpotBounty owner sign in." path="/admin/login" noindex />
      <header className="border-b border-[#2A2F34]">
        <div className="max-w-6xl mx-auto px-6 py-5"><Link to="/"><SBLogo size={18} /></Link></div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} data-testid="admin-login-form" className="sb-card p-8 w-full max-w-md">
          <div className="sb-eyebrow flex items-center gap-2"><Lock size={12} className="text-[#FFC107]" /> Owner Sign In</div>
          <h1 className="text-[28px] font-black mt-3">Admin Login</h1>
          <p className="mt-2 text-[13px] text-[#A1A1AA]">Restricted to the SpotBounty owner account.</p>

          {error && (
            <div className="mt-5 sb-card p-3 border-[#EF4444]/40 flex items-start gap-2" style={{ background: "rgba(239,68,68,.06)" }}>
              <AlertCircle size={14} className="text-[#EF4444] mt-0.5" />
              <span className="text-[12.5px] text-[#EF4444]" data-testid="admin-login-error">{error}</span>
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <label className="sb-label">Email</label>
              <div className="relative">
                <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
                <input required type="email" data-testid="admin-email" className="sb-input pl-9" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
              </div>
            </div>
            <div>
              <label className="sb-label">Password</label>
              <div className="relative">
                <Lock size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
                <input required type="password" data-testid="admin-password" className="sb-input pl-9" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </div>
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="mt-6 sb-btn-primary w-full disabled:opacity-50">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign In"}
          </button>

          <div className="mt-4 flex items-center justify-between text-[12px]">
            <button type="button" onClick={() => setShowFP(true)} className="text-[#FFC107] hover:underline" data-testid="admin-forgot-password">Forgot password?</button>
            <button type="button" onClick={() => setShowFE(true)} className="text-[#A1A1AA] hover:text-white" data-testid="admin-forgot-email">Forgot email?</button>
          </div>
        </form>
      </main>
      <ForgotPasswordModal open={showFP} onClose={() => setShowFP(false)} role="owner" />
      <ForgotEmailModal open={showFE} onClose={() => setShowFE(false)} defaultRole="owner" />
    </div>
  );
}
